import { User } from 'firebase/auth';
import {
  AppData,
  Reward as RewardType,
  calculateRewardValue,
  spendFromWallet,
  createActivityLogEntry,
  addActivityLogEntry
} from '@/utils/storage';
import { celebrateAchievement } from '@/utils/animations';
import { NativeFeatures } from '@/services/nativeFeatures';

export const useRewardManager = (
  data: AppData | null,
  updateData: (newData: AppData) => Promise<AppData>,
  setIsAnimating: (isAnimating: boolean) => void,
  setShowGiantStar: (showGiantStar: boolean) => void,
  user: User | null
) => {
  const handleSpendMoney = (reward: RewardType, amount: number) => {
    if (!data || !reward.currency) return;
    
    const starsToSpend = Math.ceil(amount / (reward.rate || 1)) * (reward.threshold || 1);
    
    if (starsToSpend > data.stars) {
      // Trigger warning haptic for insufficient stars
      NativeFeatures.hapticWarning();
      return;
    }
    
    // Trigger success haptic for successful spending
    NativeFeatures.hapticSuccess();
    
    setIsAnimating(true);
    setShowGiantStar(true);
    
    setTimeout(() => {
      let newData: AppData = {
        ...data,
        stars: data.stars - starsToSpend
      };

      // Deduct the money-equivalent of the spent stars from the spendable balance
      newData = spendFromWallet(newData, starsToSpend);

      updateData(newData).catch((error) => {
        console.error('[useRewardManager] Failed to persist spend:', error);
      });

      // Show celebration after star animation
      setTimeout(() => {
        celebrateAchievement(`Spent ${reward.currency}${amount} - ${reward.title}`);
      }, 500);
    }, 800);
  };

  const handleClaimReward = (reward: RewardType) => {
    if (!data) return;
    
    // Handle calculated rewards differently
    if (reward.type === 'calculated') {
      const calculatedValue = calculateRewardValue(reward, data.totalStarsEarned || 0);
      
      if (calculatedValue <= 0) {
        // No value to claim yet
        NativeFeatures.hapticWarning();
        return;
      }
      
      // Trigger success haptic for successful reward claim
      NativeFeatures.hapticSuccess();
      
      setIsAnimating(true);
      setShowGiantStar(true);
      
      setTimeout(() => {
        // Mark reward as claimed with timestamp
        const updatedRewards = data.rewards.map(r =>
          r.id === reward.id
            ? { ...r, claimed: true, claimedOn: new Date().toISOString() }
            : r
        );

        let newData: AppData = {
          ...data,
          rewards: updatedRewards
        };

        // Log activity if user is signed in (for family sharing)
        if (user && newData.familyId) {
          const activityEntry = createActivityLogEntry(
            user.uid,
            user.displayName || 'Unknown',
            'reward_claimed',
            {
              rewardTitle: reward.title
            }
          );
          newData = addActivityLogEntry(newData, activityEntry);
        }

        updateData(newData).catch((error) => {
          console.error('[useRewardManager] Failed to persist reward claim:', error);
        });

        // Show trophy celebration after star animation
        setTimeout(() => {
          const rewardMessage = `${reward.title} - ${reward.currency || '$'}${calculatedValue}`;
          celebrateAchievement(rewardMessage);
        }, 500);
      }, 800);
      
      return;
    }
    
    // Handle fixed cost rewards (existing logic)
    if (data.stars < reward.cost) {
      // Trigger warning haptic for insufficient stars
      NativeFeatures.hapticWarning();
      return;
    }
    
    // Trigger success haptic for successful reward claim
    NativeFeatures.hapticSuccess();
    
    setIsAnimating(true);
    setShowGiantStar(true);
    
    setTimeout(() => {
      let newData: AppData = {
        ...data,
        stars: data.stars - reward.cost
      };

      // Deduct the money-equivalent of the spent stars from the spendable balance
      newData = spendFromWallet(newData, reward.cost);

      // Log activity if user is signed in (for family sharing)
      if (user && newData.familyId) {
        const activityEntry = createActivityLogEntry(
          user.uid,
          user.displayName || 'Unknown',
          'reward_claimed',
          {
            rewardTitle: reward.title
          }
        );
        newData = addActivityLogEntry(newData, activityEntry);
      }

      updateData(newData).catch((error) => {
        console.error('[useRewardManager] Failed to persist reward claim:', error);
      });

      // Show trophy celebration after star animation
      setTimeout(() => {
        celebrateAchievement(reward.title);
      }, 500);
    }, 800);
  };

  const handleDeleteReward = (rewardId: string) => {
    if (!data) return;
    
    const updatedRewards = data.rewards.filter(r => r.id !== rewardId);
    const newData = {
      ...data,
      rewards: updatedRewards
    };

    updateData(newData).catch((error) => {
      console.error('[useRewardManager] Failed to persist reward deletion:', error);
    });
  };

  const handleMoveReward = (rewardId: string, direction: 'up' | 'down') => {
    if (!data) return;
    
    const rewardIndex = data.rewards.findIndex(r => r.id === rewardId);
    if (rewardIndex === -1) return;
    
    // Don't move if already at the top/bottom
    if (direction === 'up' && rewardIndex === 0) return;
    if (direction === 'down' && rewardIndex === data.rewards.length - 1) return;
    
    const newRewards = [...data.rewards];
    const swapIndex = direction === 'up' ? rewardIndex - 1 : rewardIndex + 1;
    
    // Swap the rewards
    [newRewards[rewardIndex], newRewards[swapIndex]] = [newRewards[swapIndex], newRewards[rewardIndex]];
    
    // Add order property if it doesn't exist
    const rewardsWithOrder = newRewards.map((reward, index) => ({
      ...reward,
      order: index
    }));
    
    const newData = {
      ...data,
      rewards: rewardsWithOrder
    };

    updateData(newData).catch((error) => {
      console.error('[useRewardManager] Failed to persist reward reorder:', error);
    });
  };

  return {
    handleClaimReward,
    handleSpendMoney,
    handleDeleteReward,
    handleMoveReward
  };
};
